import React from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { useLeads } from '../hooks/useLeads';
import { useAuth } from '../context/AuthContext';
import { 
  AlertTriangle, 
  Clock, 
  MessageSquare, 
  Phone, 
  Mail, 
  ExternalLink,
  Users
} from 'lucide-react';

interface LeadsNaoAtendidosProps {
  onViewLead?: (leadId: string) => void;
  onGoToLeads?: () => void;
}

export const LeadsNaoAtendidos: React.FC<LeadsNaoAtendidosProps> = ({ 
  onViewLead, 
  onGoToLeads 
}) => {
  const { leads, loading } = useLeads();
  const { user } = useAuth();

  // Filtrar leads não atendidos do usuário atual
  const leadsNaoAtendidos = leads.filter(lead => 
    lead.assigned_to === user?.id && 
    lead.status === 'new' && 
    lead.converted_to_client_id === null
  );

  // Separar leads dos portais (mais urgentes)
  const leadsPortal = leadsNaoAtendidos.filter(lead => 
    lead.source?.includes('Portal')
  );
  const outrosLeads = leadsNaoAtendidos.filter(lead => 
    !lead.source?.includes('Portal')
  );

  const totalNaoAtendidos = leadsNaoAtendidos.length;

  if (loading || totalNaoAtendidos === 0) {
    return null;
  }

  const formatTimeAgo = (date: string) => {
    const now = new Date();
    const leadDate = new Date(date);
    const diffMinutes = Math.floor((now.getTime() - leadDate.getTime()) / (1000 * 60));
    
    if (diffMinutes < 1) return 'Agora mesmo';
    if (diffMinutes < 60) return `${diffMinutes}min atrás`;
    
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h atrás`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d atrás`;
  };

  const getSourceBadgeColor = (source: string) => {
    if (source?.includes('Portal')) {
      return 'bg-red-100 text-red-800 border-red-300';
    }
    return 'bg-blue-100 text-blue-800 border-blue-300';
  };

  return (
    <Card className="border-l-4 border-l-red-500 shadow-lg bg-gradient-to-r from-red-50 to-orange-50 animate-pulse-subtle">
      <div className="p-6">
        {/* Header com alerta */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-full flex-shrink-0">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-red-900">
                Leads Aguardando Atendimento
              </h3>
              <p className="text-sm text-red-700">
                {totalNaoAtendidos} {totalNaoAtendidos === 1 ? 'lead precisa' : 'leads precisam'} da sua atenção
              </p>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <Badge className="bg-red-500 text-white animate-pulse text-center">
              {totalNaoAtendidos} NOVOS
            </Badge>
            <Button 
              variant="outline" 
              size="sm"
              onClick={onGoToLeads}
              className="text-red-700 border-red-300 hover:bg-red-50"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Ver Todos
            </Button>
          </div>
        </div>

        {/* Leads dos Portais - Prioridade Alta */}
        {leadsPortal.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <span className="text-sm font-medium text-red-800">
                Leads dos Portais - URGENTE
              </span>
            </div>
            <div className="space-y-2">
              {leadsPortal.slice(0, 2).map((lead) => (
                <div 
                  key={lead.id}
                  className="bg-white border border-red-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
                >
                   <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                        <h4 className="font-medium text-gray-900 truncate">{lead.name}</h4>
                        <div className="flex items-center gap-2">
                          <Badge className={getSourceBadgeColor(lead.source)}>
                            {lead.source}
                          </Badge>
                          <div className="flex items-center text-red-600 text-xs whitespace-nowrap">
                            <Clock className="h-3 w-3 mr-1" />
                            {formatTimeAgo(lead.created_at)}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-gray-600">
                        {lead.phone && (
                          <div className="flex items-center gap-1">
                            <Phone className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{lead.phone}</span>
                          </div>
                        )}
                        {lead.email && (
                          <div className="flex items-center gap-1">
                            <Mail className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{lead.email}</span>
                          </div>
                        )}
                        {lead.property_id && (
                          <div className="text-xs bg-gray-100 px-2 py-1 rounded whitespace-nowrap">
                            Imóvel: {lead.property_id}
                          </div>
                        )}
                      </div>
                      
                      {lead.message && (
                        <p className="text-sm text-gray-700 mt-2 line-clamp-2">
                          {lead.message}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex-shrink-0">
                      <Button 
                        size="sm"
                        onClick={() => onViewLead?.(lead.id)}
                        className="bg-red-600 hover:bg-red-700 text-white w-full sm:w-auto"
                      >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        Atender
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Outros Leads */}
        {outrosLeads.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Users className="h-4 w-4 text-orange-600" />
              <span className="text-sm font-medium text-orange-800">
                Outros Leads Novos
              </span>
            </div>
            <div className="space-y-2">
              {outrosLeads.slice(0, 2).map((lead) => (
                <div 
                  key={lead.id}
                  className="bg-white border border-orange-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
                >
                   <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                        <h4 className="font-medium text-gray-900 truncate">{lead.name}</h4>
                        <div className="flex items-center gap-2">
                          <Badge className={getSourceBadgeColor(lead.source)}>
                            {lead.source}
                          </Badge>
                          <div className="flex items-center text-orange-600 text-xs whitespace-nowrap">
                            <Clock className="h-3 w-3 mr-1" />
                            {formatTimeAgo(lead.created_at)}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-gray-600">
                        {lead.phone && (
                          <div className="flex items-center gap-1">
                            <Phone className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{lead.phone}</span>
                          </div>
                        )}
                        {lead.email && (
                          <div className="flex items-center gap-1">
                            <Mail className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{lead.email}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex-shrink-0">
                      <Button 
                        variant="outline"
                        size="sm"
                        onClick={() => onViewLead?.(lead.id)}
                        className="text-orange-700 border-orange-300 hover:bg-orange-50 w-full sm:w-auto"
                      >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        Atender
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Mostrar se há mais leads */}
        {totalNaoAtendidos > 4 && (
          <div className="mt-4 text-center">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={onGoToLeads}
              className="text-red-700 hover:bg-red-50"
            >
              <Users className="h-4 w-4 mr-2" />
              Ver mais {totalNaoAtendidos - 4} leads não atendidos
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};